import type { VideoVisibility } from 'js/src/client';
import { ApiService, VideoStatus } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { loadProjects } from 'js/src/data/account';
import {
    setLoadingFinished,
    setVideoStatus,
    setVideoVisibility,
    startAutoDownload,
    storeRenderEstimate,
    storeVideo,
} from 'js/src/data/share-video/share-video-actions';
import { selectVideoVisibility } from 'js/src/data/share-video/share-video-selectors';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { localStorageGet, LocalStorageKeys } from 'js/src/libs/local-storage';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';

function _getSlugFromPrettyPath(): string {
    const match = /.*\/.*-([a-zA-Z\d]+)\//.exec(window.location.pathname);
    if (match === null || match.length < 2 || match[1].length < 3) {
        throw new Error('Could not find video ID');
    }
    return match[1];
}

function _getVideoSlug(): string {
    const match = /\/(?:v|embed)\/(.*)\//.exec(window.location.pathname);
    if (match === null || match.length < 2 || match[1].length < 3) {
        // try the pretty path
        return _getSlugFromPrettyPath();
    }
    return match[1];
}

export function loadVideo(autoDownload = false) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const response = await ApiService.publicVideoRetrieve({
            path: { slug: _getVideoSlug() },
        });
        const publicVideo = response.data;
        dispatch(storeVideo(publicVideo));
        if (SERVER.USER) {
            dispatch(loadProjects());
        }

        dispatch(setLoadingFinished());

        if (autoDownload) {
            dispatch(startAutoDownload());
        }
    };
}

/**
 * Must be called after a video has been loaded and saved (loadVideo above)
 */
export function loadRenderDetails() {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        let response;
        try {
            const videoId = getState().shareVideo.video?.id;
            response = await ApiService.queueVideoRenderDetailsRetrieve({
                path: { video_id: `${videoId}` },
            });
        } catch (error) {
            ErrorReporter.capture(error);
            return;
        }
        const videoStatus = response.data.video_status;
        const { estimated_status, estimated_time_remaining } = response.data;
        dispatch(storeRenderEstimate({ estimated_status, estimated_time_remaining }));
        dispatch(setVideoStatus(videoStatus));

        if (videoStatus === VideoStatus.PUBLISHED) {
            // A queued carousel download published the video purely to get renderUrls; handing the
            // user an mp4 as well is not what they asked for.
            const autoDownload =
                !localStorageGet(LocalStorageKeys.DISABLE_AUTO_DOWNLOAD) &&
                !getState().shareVideo.queuedArtifactDownload;
            try {
                await dispatch(loadVideo(autoDownload));
            } catch (error) {
                ErrorReporter.capture(error);
            }
        }
    };
}

export function updateVideoVisibility(visibility: VideoVisibility, videoId: number) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        // preemptively update the visibility to keep it feeling snappy
        const oldVisibility = selectVideoVisibility(getState());
        dispatch(setVideoVisibility(visibility));

        ApiService.videoPartialUpdate({
            path: { video_id: videoId.toString() },
            body: { visibility },
        })
            .then((response) => {
                // Make sure we show what the server says
                dispatch(setVideoVisibility(response.data.visibility));
            })
            .catch((err: unknown) => {
                Toast.error(
                    'Something went wrong with changing the access to this video, please try again.',
                );
                dispatch(setVideoVisibility(oldVisibility));
                ErrorReporter.capture(err);
            });

        // analytics tracking for the visibility change
        analytics.track('Video visibility changed', { visibility: visibility });
    };
}
